import React, { useMemo, useState } from "react";
import { Badge, Table } from "reactstrap";

// Backend mapper renames *Snapshot fields:
//   productTitleSnapshot → productTitle
//   variantTitleSnapshot → variantTitle

const STATUS_COLOR = {
  COMPLETED: "success",
  EXTRA:     "danger",
  PENDING:   "secondary",
};

const STATUS_LABEL = {
  COMPLETED: "Done",
  EXTRA:     "Extra",
  PENDING:   "Remaining",
};

function StockCheckItemsTable({ items = [], activeTab = "ALL", unknownBarcodes = [] }) {
  const [hideCompleted, setHideCompleted] = useState(false);

  const filtered = useMemo(() => {
    let list = items;
    if (activeTab === "REMAINING")  list = list.filter((i) => i.status === "PENDING");
    if (activeTab === "COMPLETED")  list = list.filter((i) => i.status === "COMPLETED");
    if (activeTab === "EXTRA")      list = list.filter((i) => i.status === "EXTRA");
    if (activeTab === "UNKNOWN")    return [];
    if (hideCompleted && activeTab === "ALL") {
      list = list.filter((i) => i.status !== "COMPLETED");
    }
    return list;
  }, [items, activeTab, hideCompleted]);

  if (activeTab === "UNKNOWN") {
    return unknownBarcodes.length > 0 ? (
      <div className="sc-unknown-tags">
        {unknownBarcodes.map((u) => (
          <Badge key={u.barcode} color="warning" pill>
            {u.barcode}
            {u.scanCount > 1 && (
              <span style={{ marginLeft: 4, opacity: 0.7 }}>×{u.scanCount}</span>
            )}
          </Badge>
        ))}
      </div>
    ) : (
      <p className="sc-tab-empty">
        Unknown barcodes appear here once the session is completed and a manager views the report.
      </p>
    );
  }

  if (filtered.length === 0) {
    return <p className="sc-tab-empty">No items to show.</p>;
  }

  return (
    <div>
      {activeTab === "ALL" && (
        <label className="sc-toggle-label" htmlFor="sc-hide-completed">
          <input
            type="checkbox"
            id="sc-hide-completed"
            checked={hideCompleted}
            onChange={(e) => setHideCompleted(e.target.checked)}
            style={{ cursor: "pointer", marginRight: 2 }}
          />
          Show remaining only
        </label>
      )}

      <div style={{ overflowX: "auto" }}>
        <Table responsive size="sm" style={{ marginBottom: 0, fontSize: "0.85rem" }}>
          <thead className="text-primary">
            <tr>
              <th style={{ minWidth: 120 }}>Product</th>
              <th style={{ minWidth: 80 }}>Variant</th>
              <th style={{ width: 64, textAlign: "right" }}>Expected</th>
              <th style={{ width: 64, textAlign: "right" }}>Scanned</th>
              <th style={{ width: 52, textAlign: "right" }}>Left</th>
              <th style={{ width: 88 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item) => {
              const remaining = Math.max(0, (item.expectedQty ?? 0) - (item.scannedQty ?? 0));
              return (
                <tr key={item.id}>
                  <td className="sp-truncate" style={{ maxWidth: 180 }}>
                    {item.productTitle || "—"}
                  </td>
                  <td className="sp-truncate" style={{ maxWidth: 120 }}>
                    {item.variantTitle || "—"}
                  </td>
                  <td style={{ textAlign: "right" }}>{item.expectedQty ?? 0}</td>
                  <td style={{ textAlign: "right" }}>{item.scannedQty ?? 0}</td>
                  <td style={{ textAlign: "right" }}>{remaining}</td>
                  <td>
                    <Badge color={STATUS_COLOR[item.status] ?? "secondary"} pill>
                      {STATUS_LABEL[item.status] ?? item.status}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

export default StockCheckItemsTable;
